import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Badge } from '@mobi/component-library/Feedback/Badge'
import { Banner } from '@mobi/component-library/Feedback/Banner'
import { LoadingSpinner } from '@mobi/component-library/Feedback/LoadingSpinner'
import { spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { TabGroupStyled } from '@mobi/component-library/Navigation/Tabs/Components/Common.styles'
import { useSearchParamState } from '@mobi/utils/hooks/useSearchParamState'
import { MainContainer } from '@core/Components/Containers'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { useAppSeo } from '@core/Utils/hooks/useAppSeo'
import { StructuredSearchCriteria } from './Components/Criteria/StructuredSearchCriteria'
import { StructuredSearchResults } from './Components/Results/StructuredSearchResults'
import { setSelectedTracks } from './Store'
import { selectSearchCriteria } from './Store/selectors'
import { buildStructuredSearchUrl } from './helpers/api/buildStructuredSearchUrl'
import { getStructuredSearchResults } from './helpers/api/getStructuredSearchResults'

export const StructuredSearchPage: React.FC = () => {
  useAppSeo({
    title: 'Smart Form Racing Form & Tips | TABtouch',
    description:
      'Analyse racing form, runners and betting insights using TABtouch smart form tools.',
  })
  const dispatch = useDispatch()

  const [selectedTab, setSelectedTab] = useSearchParamState<'Criteria' | 'Results'>({
    key: 'tab',
    values: ['Criteria', 'Results'],
    defaultValue: 'Criteria',
  })

  const currentSearchCriteria = useSelector(selectSearchCriteria)

  const searchQueryParams = buildStructuredSearchUrl(currentSearchCriteria)

  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: [queryKeys.structuredSearchBase, searchQueryParams],
    queryFn: () => getStructuredSearchResults(searchQueryParams),
    gcTime: 1_000 * 60 * 2,
    staleTime: 1_000 * 60 * 2,
  })

  React.useEffect(() => {
    if (isLoading) return
    const availableTracks = data?.aggregations?.tracks
    const selectedTrackIds = currentSearchCriteria.selectedTracks
    if (availableTracks && selectedTrackIds) {
      // Only allow selected tracks from available
      const availableSelectedTrackIds = availableTracks.flatMap(({ id }) =>
        selectedTrackIds.includes(id) ? [id] : []
      )
      dispatch(setSelectedTracks(availableSelectedTrackIds))
    }
  }, [data?.aggregations?.tracks, currentSearchCriteria.selectedTracks, isLoading, dispatch])

  const totalResultCount = data?.statistics.hitCount || 0

  return (
    <MainContainer data-tid-structured-search-container background='white'>
      <PageHeader title='Smart Form' />

      <TabGroupStyled>
        <Tabs bottomBorder='none'>
          <TabButton
            content='Criteria'
            isSelected={selectedTab === 'Criteria'}
            onClick={() => setSelectedTab('Criteria')}
            testId='SmartForm.Criteria'
          />

          <TabButton
            content={
              <Flex gap={spacing.smx2}>
                Results
                {isLoading ? (
                  <LoadingSpinner size='1.6rem' color='purple' />
                ) : (
                  <Badge color='purple'>{totalResultCount}</Badge>
                )}
              </Flex>
            }
            isSelected={selectedTab === 'Results'}
            onClick={() => setSelectedTab('Results')}
            testId='SmartForm.Results'
          />
        </Tabs>

        {isError && (
          <Banner
            text='Sorry, unable to retrieve search results'
            type='error'
            button={
              <Button color='primary_destructive' size='xxs' onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        )}
      </TabGroupStyled>

      {selectedTab === 'Criteria' && (
        <StructuredSearchCriteria
          setResultsTab={() => setSelectedTab('Results')}
          totalResultCount={totalResultCount}
          availableTracks={data?.aggregations?.tracks}
        />
      )}

      {selectedTab === 'Results' && <StructuredSearchResults results={data?.results || []} />}
    </MainContainer>
  )
}
